import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { ProductImage } from "@/features/common/components/ProductImage";
import { SaleVolumeGraph } from "@/features/common/components/SaleVolumeGraph";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { ChartHistoryDurationSelector } from "@/features/common/components/ChartHistoryDurationSelector";
import { StandardLineChart } from "@/features/common/components/StandardLineChart";
import { useProduct, type VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { useProductHistoricalPricing } from "@/features/products/api/useProductHistoricalPricing";
import { getProductHistoricalPricingChartConfig } from "@/features/products/getProductHistoricalPricingChartConfig";
import { getProductHistoricalPricingChartData } from "@/features/products/getProductHistoricalPricingChartData";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { useMemo, type FC, type PropsWithChildren } from "react";
import { cn } from "@/ds/utils";
import { useFindInCollections, type FindInCollectionsResponseModel } from "@/features/products/api/useFindInCollections";
import { SmallCollectionDisplay } from "@/features/common/components/SmallCollectionDisplay";
import { collectionArts } from "@/lib/media/collectionArts";
import { Link, useNavigate } from "react-router";
import { LoadingState } from "@/ds/custom/LoadingState";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";

export interface ProductViewProps {
	productId: string;
	className?: string;
	hideChartHistoryDurationSelector?: boolean;
}

export const ProductView: FC<ProductViewProps> = props => {
	const {
		productId,
		className,
		hideChartHistoryDurationSelector = false
	} = props;

	const product = useProduct(productId);
	const { state } = useUserPreferencesState();
	const productHistoricalPricing = useProductHistoricalPricing(productId, state.chartHistoryDuration);
	const chartData = useMemo(() => getProductHistoricalPricingChartData(productHistoricalPricing.data || []), [productHistoricalPricing.data]);
	const chartConfig = useMemo(() => getProductHistoricalPricingChartConfig(product.data?.variants || []), [product.data]);
	const variantAdditionalProps = useMemo<PageProductVariantsAdditionalProps | null>(() => !product.data ? null : {
		imageUrl: product.data.imageUrl
	}, [product.data]);

	const findInCollections = useFindInCollections(productId);

	if (product.isError) {
		return (
			<div className={cn("p-3", className)}>
				<ErrorDisplay error={product.error} />
			</div>
		);
	}

	if (product.isLoading) {
		return <LoadingState />;
	}

	return (
		<div className={cn("flex flex-col @md:flex-row", className)}>
			<div className="@md:border-r @md:max-w-78.25 grow">
				<div className="p-3 border-b flex justify-center">
					{
						product.data &&
						<ProductImage imageUrl={product.data.imageUrl} className="w-72 rounded-3xl" />
					}
				</div>

				<div className="p-3 gap-3 flex flex-col border-b @md:grid @md:grid-cols-[repeat(auto-fit,minmax(64px,1fr))]">
					{product.data?.rarity && <ProductAttribute name="Rarity">{product.data.rarity}</ProductAttribute>}
					{product.data?.talent && <ProductAttribute name="Talent">{product.data.talent}</ProductAttribute>}
					{product.data?.cardType && <ProductAttribute name="Card Type">{product.data.cardType}</ProductAttribute>}
					{product.data?.cardSubType && <ProductAttribute name="Card Subtype">{product.data.cardSubType}</ProductAttribute>}
					{product.data?.pitchValue && <ProductAttribute name="Pitch Value">{product.data.pitchValue}</ProductAttribute>}
					{product.data?.class && <ProductAttribute name="Class">{product.data.class}</ProductAttribute>}
					{product.data?.cost && <ProductAttribute name="Cost">{product.data.cost}</ProductAttribute>}
					{product.data?.power && <ProductAttribute name="Power">{product.data.power}</ProductAttribute>}
					{product.data?.defenseValue && <ProductAttribute name="Defense Value">{product.data.defenseValue}</ProductAttribute>}
				</div>
			</div>
			<div className="grow">
				{!hideChartHistoryDurationSelector && <ChartHistoryDurationSelector />}
				<StandardLineChart data={chartData} config={chartConfig} xAxisDataKey="date" />

				{
					variantAdditionalProps &&
					<>
						<div className="border-b">
							<DataView
								onlyUseGalleryForMobile
								items={product.data?.variants || []}
								getKey={x => x.variantId}
								listViewHeaderLabels={["Variant", "Sale Volumes", "Price"]}
								listViewHeaderClassNames={["min-w-0", "w-48 @2xl:w-72", "w-31 @2xl:w-64"]}
								listComponent={PageProductVariantsListItem}
								galleryComponent={PageProductVariantsGalleryItem}
								additionalProps={variantAdditionalProps}
								galleryMinWidth={208} />
						</div>

						{
							findInCollections.data && findInCollections.data.length > 0 &&
							<DataView
								onlyUseGalleryForMobile
								items={findInCollections.data || []}
								getKey={x => `${x.collectionId}-${x.variantId}`}
								listViewHeaderLabels={["Collection", "Variant", "Quantity"]}
								listViewHeaderClassNames={["min-w-0", "w-56 @2xl:w-64", "w-22 @2xl:w-24"]}
								listComponent={PageProductFindInCollectionsListItem}
								galleryComponent={PageProductFindInCollectionsGalleryItem}
								galleryMinWidth={208} />
						}
					</>
				}
			</div>
		</div>
	);
};

interface PageProductVariantsAdditionalProps {
	imageUrl: string;
}

const PageProductVariantsListItem: FC<VariantMarketPriceResponseModel & PageProductVariantsAdditionalProps> = props => {
	const {
		imageUrl,
		variantName,
		fcId,
		saleVolumes,
		marketPrice
	} = props;

	return (
		<ListItem>
			<TableCell>
				<SmallProductDisplay imageUrl={imageUrl} productName={variantName} fcId={fcId} />
			</TableCell>
			<TableCell>
				<SaleVolumeGraph data={saleVolumes} />
			</TableCell>
			<TableCell>
				{
					marketPrice
						? <MoneyDisplay mode="Standard" money={marketPrice} />
						: "-"
				}
			</TableCell>
		</ListItem>
	);
};

const PageProductVariantsGalleryItem: FC<VariantMarketPriceResponseModel & PageProductVariantsAdditionalProps> = props => {
	const {
		imageUrl,
		variantName,
		fcId,
		marketPrice,
		saleVolumes
	} = props;

	return (
		<GalleryItem title={variantName} description={fcId} imageUrl={imageUrl}>
			<GalleryItemProperty label="Price">
				{marketPrice ? <MoneyDisplay mode="Standard" money={marketPrice} /> : "-"}
			</GalleryItemProperty>
			<GalleryItemProperty>
				<SaleVolumeGraph data={saleVolumes} />
			</GalleryItemProperty>
		</GalleryItem>
	);
};

const PageProductFindInCollectionsListItem: FC<FindInCollectionsResponseModel> = props => {
	const {
		collectionId,
		variantId,
		collectionName,
		collectionImage,
		variantName,
		quantity
	} = props;

	return (
		<ListItem className="relative hover:bg-background-highlight dark:even:hover:bg-background-highlight transition-colors duration-150">
			<TableCell>
				<Link to={`/collections/${collectionId}/transactions/${variantId}`} className="after:absolute after:inset-0">
					<SmallCollectionDisplay name={collectionName} image={collectionImage} />
				</Link>
			</TableCell>
			<TableCell>
				{variantName}
			</TableCell>
			<TableCell>
				{quantity}
			</TableCell>
		</ListItem>
	);
};

const PageProductFindInCollectionsGalleryItem: FC<FindInCollectionsResponseModel> = props => {
	const {
		collectionId,
		variantId,
		collectionName,
		collectionImage,
		variantName,
		quantity
	} = props;

	const navigate = useNavigate();

	return (
		<GalleryItem
			title={collectionName}
			description={variantName}
			imageUrl={collectionArts[collectionImage]}
			isProductImage={false}
			onClick={() => navigate(`/collections/${collectionId}/transactions/${variantId}`)}>
			<GalleryItemProperty label="Quantity">{quantity}</GalleryItemProperty>
		</GalleryItem>
	);
};

interface ProductAttributeProps extends PropsWithChildren {
	name: string;
}

const ProductAttribute: FC<ProductAttributeProps> = props => {
	const {
		name,
		children
	} = props;

	return (
		<div>
			<div className="type-overline">{name}</div>
			<div className="text-sm">
				{children}
			</div>
		</div>
	);
};
